import { ClipboardPaste } from 'lucide-react';
import { useRef } from 'react';

import { terminalManager } from '../lib/terminalManager';
import { useSettings } from '../stores/settingsStore';
import { useTerminalRuntime } from '../stores/terminalRuntimeStore';
import { Modal } from './Modal';

const PREVIEW_LINES = 10;

/** Confirmation shown when a paste contains newlines — multiline text can
 *  start executing the moment it hits the shell. Mounted once at the root;
 *  renders only while terminalManager has parked a pending paste. */
export function PasteWarningDialog() {
    const pendingPaste = useTerminalRuntime((s) => s.pendingPaste);
    const clearPendingPaste = useTerminalRuntime((s) => s.clearPendingPaste);
    const setWarnMultilinePaste = useSettings((s) => s.setWarnMultilinePaste);
    const dontAskRef = useRef(false);

    if (!pendingPaste) return null;

    const lines = pendingPaste.text.split(/\r\n|\r|\n/);
    const preview = lines.slice(0, PREVIEW_LINES).join('\n');
    const hidden = lines.length - PREVIEW_LINES;

    const cancel = () => clearPendingPaste();
    const confirm = () => {
        if (dontAskRef.current) setWarnMultilinePaste(false);
        terminalManager.pasteText(pendingPaste.sessionId, pendingPaste.text);
        clearPendingPaste();
        terminalManager.focus(pendingPaste.sessionId);
    };

    return (
        <Modal
            title="Paste multiple lines?"
            sub={`${lines.length} lines — the shell may run them immediately`}
            onClose={cancel}
            width={460}
            foot={
                <>
                    <button
                        className="px-2.5 py-2 rounded-lg border border-border bg-bg-2 text-text-dim text-[12.5px] hover:text-text hover:border-border-strong hover:bg-bg-3 transition-colors duration-100"
                        onClick={cancel}
                    >
                        Cancel
                    </button>
                    <button
                        autoFocus
                        className="inline-flex items-center justify-center gap-1.75 px-3 py-2 rounded-lg bg-accent text-bg-0 text-[12.5px] font-medium transition-[background,transform] duration-100 hover:brightness-110 active:translate-y-px"
                        onClick={confirm}
                    >
                        <ClipboardPaste size={13} />
                        Paste
                    </button>
                </>
            }
        >
            <pre className="m-0 font-mono text-[11.5px] text-text-dim bg-bg-2 border border-border rounded-md p-2 max-h-40 overflow-auto whitespace-pre">
                {preview}
                {hidden > 0 && (
                    <span className="text-text-faint">{`\n… ${hidden} more line${hidden === 1 ? '' : 's'}`}</span>
                )}
            </pre>
            <label className="flex items-center gap-2 text-[11.5px] text-text-muted cursor-pointer select-none">
                <input
                    type="checkbox"
                    className="accent-accent"
                    onChange={(e) => {
                        dontAskRef.current = e.target.checked;
                    }}
                />
                Don&apos;t ask again (re-enable in Settings → Terminal)
            </label>
        </Modal>
    );
}
